import { Skeleton } from '@components/ui/skeleton';
import { motion, useReducedMotion } from 'framer-motion';

import { cn } from '@/lib/utils';

type Props = {
  index: number;
  isFirst?: boolean;
  isLast?: boolean;
  className?: string;
};

export const GenerationSkeleton = ({
  index,
  isFirst,
  isLast,
  className
}: Props) => {
  const reduceMotion = useReducedMotion();
  const transition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.4, delay: index * 0.05 };

  return (
    <motion.div
      animate={{ opacity: 1 }}
      className={cn(
        'border border-black/[0.08] border-b-0 bg-white p-4 text-sm',
        isFirst && 'rounded-t-md',
        isLast && 'rounded-b-md border-b',
        className
      )}
      initial={reduceMotion ? { opacity: 1 } : { opacity: 0 }}
      transition={transition}
    >
      <div className="grid w-full grid-cols-[8rem_10rem_9rem_1fr] items-center gap-4">
        {/* ID + Environment — two lines of leading-5 text */}
        <div className="space-y-[3px]">
          <Skeleton className="h-[18px] w-16" />
          <Skeleton className="h-[18px] w-12" />
        </div>

        {/* Workspace + version */}
        <div className="space-y-[3px]">
          <div className="flex items-center gap-1.5">
            <Skeleton className="size-2.5 shrink-0 rounded-full" />
            <Skeleton className="h-[18px] w-20" />
          </div>
          <Skeleton className="h-[18px] w-16 rounded-md" />
        </div>

        {/* Status + duration */}
        <div className="space-y-[3px]">
          <div className="flex items-center gap-2">
            <Skeleton className="size-3.5 shrink-0 rounded-full" />
            <Skeleton className="h-[18px] w-14" />
          </div>
          <Skeleton className="ml-6 h-[18px] w-20" />
        </div>

        {/* User */}
        <div className="justify-self-end">
          <div className="flex items-center gap-2">
            <Skeleton className="h-[18px] w-20" />
            <Skeleton className="size-6 rounded-full" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};
